/**
 * App versions and updates (design: "About & updates" canvas, approved 2026-10-29). Shared by every platform:
 *
 * - which version/channel line About shows, and the text "Copy version info" copies;
 * - the update status every surface renders (About's status box, the Help menu row, the macOS menu item);
 * - when it is safe to install an update and relaunch on its own (desktop) or reload silently (web) — Chrome and
 *   Slack's model: download quietly, apply only while nobody is using the app, never interrupt;
 * - what "Check for updates" does on iOS and Android (store page, or the direct APK from bundle.direct).
 *
 * Pure functions only; the platforms supply the clock and the activity they can see.
 */

// ---------- Versions ----------

/** Compares dotted numeric versions ("0.2.10" > "0.2.9"); a pre-release suffix ("-beta.1") sorts before the release. */
export function compareVersions(a: string, b: string): number {
  const split = (v: string): { nums: number[]; pre: string } => {
    const [main = "", pre = ""] = v.trim().replace(/^v/i, "").split("-", 2);
    return { nums: main.split(".").map((n) => Number.parseInt(n, 10) || 0), pre };
  };
  const x = split(a);
  const y = split(b);
  for (let i = 0; i < Math.max(x.nums.length, y.nums.length); i++) {
    const d = (x.nums[i] ?? 0) - (y.nums[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  if (x.pre === y.pre) return 0;
  if (x.pre === "") return 1;
  if (y.pre === "") return -1;
  return x.pre < y.pre ? -1 : 1;
}

export function isNewer(candidate: string, current: string): boolean {
  return compareVersions(candidate, current) > 0;
}

/** Short commit for display ("e3d6853"); "" when unknown. */
export function shortCommit(commit: string | null | undefined): string {
  const c = (commit ?? "").trim();
  return /^[0-9a-f]{7,40}$/i.test(c) ? c.slice(0, 7).toLowerCase() : "";
}

/** Where this build came from. Shown as "Stable", "TestFlight" or "Direct" (approved names). */
export type Channel = "stable" | "testflight" | "direct" | "dev";

/** One build that makes up what the person is running. */
export type BuildInfo = { version?: string; build?: string; commit?: string | null; builtAt?: string | null };

/** Everything About shows. `app` is the native shell (desktop, iOS, Android); `web` the web build; `server` the API. */
export type VersionInfo = {
  platform: "web" | "desktop" | "ios" | "android";
  channel: Channel;
  app?: BuildInfo & { os?: string };
  web?: BuildInfo;
  server?: BuildInfo;
};

/** The version in About's title line: the shell's on native apps, the web build's on the web. */
export function displayVersion(info: VersionInfo): string {
  // The web build has no release number of its own: its commit names it.
  const v = info.platform === "web" ? info.web?.version || shortCommit(info.web?.commit) : info.app?.version;
  if (v === undefined || v === "") return "";
  const build = info.app?.build;
  return (info.platform === "ios" || info.platform === "android") && build !== undefined && build !== "" ? `${v} (${build})` : v;
}

/** "Oct 29, 14:02" style, in the viewer's zone; "" when unknown or unparseable. */
export function formatBuiltAt(iso: string | null | undefined, locale: string, timeZone?: string): string {
  if (iso === undefined || iso === null || iso === "") return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone }).format(t);
}

/** "e3d6853 · Oct 29, 14:02" (either part may be missing). */
export function formatBuild(b: BuildInfo | undefined, locale: string, timeZone?: string): string {
  if (b === undefined) return "";
  return [shortCommit(b.commit), formatBuiltAt(b.builtAt, locale, timeZone)].filter((s) => s !== "").join(" · ");
}

/** What "Copy version info" puts on the clipboard: plain lines, English labels (support reads it), no PII. */
export function versionReport(info: VersionInfo): string {
  const lines = [`Bundle ${displayVersion(info)} (${info.platform}, ${info.channel})`.replace("Bundle  (", "Bundle (")];
  const row = (label: string, b: BuildInfo | undefined, extra?: string) => {
    if (b === undefined) return;
    const parts = [b.version, b.build !== undefined && b.build !== "" ? `build ${b.build}` : undefined, shortCommit(b.commit) || undefined, b.builtAt ?? undefined, extra].filter(
      (p): p is string => p !== undefined && p !== "",
    );
    if (parts.length > 0) lines.push(`${label}: ${parts.join(" · ")}`);
  };
  row("App", info.app, info.app?.os);
  row("Web", info.web);
  row("Server", info.server);
  return lines.join("\n");
}

// ---------- Update status (desktop updater, web build check, direct APK) ----------

export type UpdateStatus =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "upToDate"; version: string; checkedAt: number }
  | { kind: "downloading"; version: string; notes?: string | null; downloaded: number; total?: number | null }
  | { kind: "ready"; version: string; notes?: string | null }
  | { kind: "error"; during: "check" | "download" | "install"; message?: string };

/** Download progress 0–100, or undefined while the size is unknown. */
export function progressPercent(s: UpdateStatus): number | undefined {
  if (s.kind !== "downloading" || s.total === undefined || s.total === null || s.total <= 0) return undefined;
  return Math.max(0, Math.min(100, Math.floor((s.downloaded / s.total) * 100)));
}

/** The Help menu / macOS menu row: Check for updates… → Downloading 0.2.21… 42% → Restart to update. */
export type UpdateMenuRow = { kind: "check" } | { kind: "downloading"; version: string; percent?: number } | { kind: "restart"; version: string };

export function menuRow(s: UpdateStatus): UpdateMenuRow {
  if (s.kind === "downloading") {
    const percent = progressPercent(s);
    return percent === undefined ? { kind: "downloading", version: s.version } : { kind: "downloading", version: s.version, percent };
  }
  if (s.kind === "ready") return { kind: "restart", version: s.version };
  return { kind: "check" };
}

/** The badge dot on the Help button: an update is downloaded and waiting. */
export function showsBadge(s: UpdateStatus): boolean {
  return s.kind === "ready";
}

/** Release notes under an available version (approved: short). First lines, at most `max` characters. */
export function shortNotes(notes: string | null | undefined, max = 160): string {
  const text = (notes ?? "").replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max / 2 ? cut.slice(0, space) : cut).replace(/[\s,;:.]+$/, "")}…`;
}

// ---------- Safe to restart ----------

/** No input in the app for this long counts as "away" (Chrome relaunches after a similar quiet spell). */
export const IDLE_BEFORE_RESTART_MS = 10 * 60 * 1000;
/** Overnight (local time, [start, end) hours): a shorter quiet spell is enough. */
export const OVERNIGHT_HOURS: readonly [number, number] = [1, 5];
export const OVERNIGHT_IDLE_MS = 2 * 60 * 1000;

/** What a window/tab can see about its own use. */
export type AppActivity = {
  /** Last keyboard, pointer or touch input in this window (ms epoch); 0 = none since load. */
  lastInputAt: number;
  /** The window/tab is hidden (minimized, closed to the tray, another tab in front). */
  hidden: boolean;
  /** In a huddle, call or meeting (joining counts). */
  inCall: boolean;
  /** Text in a composer that hasn't been sent (message, thread, edit or mail composer). */
  composerText: boolean;
  /** Messages or mail waiting to send, and other writes in flight. */
  pendingWrites: number;
  /** File uploads in progress. */
  uploads: number;
};

export type RestartBlocker = "call" | "composer" | "sending" | "upload";

/** What would be lost or interrupted by a restart right now (empty = nothing). */
export function restartBlockers(a: AppActivity): RestartBlocker[] {
  const out: RestartBlocker[] = [];
  if (a.inCall) out.push("call");
  if (a.composerText) out.push("composer");
  if (a.pendingWrites > 0) out.push("sending");
  if (a.uploads > 0) out.push("upload");
  return out;
}

/** Local hour of `now` in `timeZone` (default: the runtime's). */
function localHour(now: number, timeZone?: string): number {
  const h = new Intl.DateTimeFormat("en-US", { hour: "numeric", hourCycle: "h23", timeZone }).format(now);
  return Number.parseInt(h, 10) % 24;
}

export function isOvernight(now: number, timeZone?: string): boolean {
  const h = localHour(now, timeZone);
  return h >= OVERNIGHT_HOURS[0] && h < OVERNIGHT_HOURS[1];
}

/** Nobody is using this window: hidden, or no input for 10 minutes (2 overnight). */
export function isAway(a: AppActivity, now: number, timeZone?: string): boolean {
  if (a.hidden) return true;
  const quiet = now - a.lastInputAt;
  return quiet >= IDLE_BEFORE_RESTART_MS || (isOvernight(now, timeZone) && quiet >= OVERNIGHT_IDLE_MS);
}

/** Safe to install and relaunch (desktop) or reload (web) without asking: away, and nothing a restart would break. */
export function safeToRestart(a: AppActivity, now: number, timeZone?: string): boolean {
  return restartBlockers(a).length === 0 && isAway(a, now, timeZone);
}

/** A window's last report, as the desktop shell keeps it (one per window), and whether the window is hidden now. */
export type WindowReport = { safe: boolean; at: number; hidden?: boolean };

/** A report older than this is stale (the window stopped reporting: treat as not safe). Hidden webviews tick ~1/min. */
export const REPORT_TTL_MS = 5 * 60 * 1000;

/**
 * Desktop: install the downloaded update now? Every open window must say it is safe in a fresh report; with no
 * windows at all (everything closed), yes. A hidden window's timers may be suspended by the OS, so its last report
 * stands however old. The Rust shell mirrors this (src/updater.rs `all_windows_safe`).
 */
export function allWindowsSafe(reports: readonly WindowReport[], now: number): boolean {
  return reports.every((r) => r.safe && (r.hidden === true || now - r.at <= REPORT_TTL_MS));
}

// ---------- Web: a newer build is deployed ----------

/** What /version.json says (apps/web build). */
export type WebBuild = { version?: string; commit?: string | null; builtAt?: string | null };

/** A newer web build is live: the commits differ (deploys are always newer; a rollback is new to this tab too). */
export function webBuildChanged(running: WebBuild, live: WebBuild | undefined): boolean {
  const a = shortCommit(running.commit);
  const b = shortCommit(live?.commit);
  return a !== "" && b !== "" && a !== b;
}

/** "Updated to Bundle 0.2.21" toast after a restart/reload: the version changed since the last run on this device. */
export function updatedSince(lastRun: string | undefined, current: string): boolean {
  return lastRun !== undefined && lastRun !== "" && lastRun !== current;
}

// ---------- iOS and Android: Check for updates ----------

export const TESTFLIGHT_URL = "itms-beta://";
export const PLAY_PACKAGE = "xyz.bigsmall.bundle";
export const RELEASES_MANIFEST_URL = "https://bundle.direct/releases/manifest.json";
export const RELEASES_BASE_URL = "https://bundle.direct/releases/";

/**
 * iOS: TestFlight builds open TestFlight; App Store builds open the store page from manifest.json `ios.appStoreUrl`
 * (null until the app is listed, then TestFlight too).
 */
export function iosUpdateUrl(channel: Channel, appStoreUrl?: string | null): string {
  return channel !== "testflight" && appStoreUrl !== undefined && appStoreUrl !== null && appStoreUrl !== "" ? appStoreUrl : TESTFLIGHT_URL;
}

/** Android: the installer decides. com.android.vending = Google Play; anything else is a direct APK. */
export function androidChannel(installer: string | null | undefined): Channel {
  return installer === "com.android.vending" ? "stable" : "direct";
}

export function playStoreUrl(pkg: string = PLAY_PACKAGE): string {
  return `market://details?id=${pkg}`;
}

/** The `android` entry of bundle.direct/releases/manifest.json. */
export type ApkRelease = { version: string; versionCode?: number; path: string; size?: number; sha256?: string; notes?: string | null };

/** A newer direct APK than the running one (versionCode wins when both have it), with its absolute URL. */
export function apkUpdate(
  manifest: { android?: Partial<ApkRelease> | null } | null | undefined,
  running: { version: string; versionCode?: number },
): (ApkRelease & { url: string }) | undefined {
  const a = manifest?.android;
  if (a === undefined || a === null || typeof a.version !== "string" || typeof a.path !== "string") return undefined;
  const newer =
    typeof a.versionCode === "number" && running.versionCode !== undefined ? a.versionCode > running.versionCode : isNewer(a.version, running.version);
  if (!newer) return undefined;
  const url = a.path.startsWith("https://") ? a.path : `${RELEASES_BASE_URL}${a.path.replace(/^\/+/, "")}`;
  return { ...(a as ApkRelease), url };
}

/** Direct APK: post the "Bundle x is available" notification at most once a day per version. */
export const APK_NOTIFY_EVERY_MS = 24 * 60 * 60 * 1000;

export function shouldNotifyApk(version: string, last: { version: string; at: number } | undefined, now: number): boolean {
  return last === undefined || last.version !== version || now - last.at >= APK_NOTIFY_EVERY_MS;
}
